import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { BotService } from '@/services/bot';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';

/** Quick action button shown on the widget teaser */
interface TeaserQuickAction {
  /** Button label */
  text: string;
  /** Intent triggered when the button is clicked */
  intent: string;
}

/** Widget teaser configuration passed to window.moin.teaser() */
interface TeaserConfig {
  /** Whether the teaser bubble is shown */
  showTeaser: boolean;
  /** Whether the avatar is shown next to the teaser */
  showAvatar: boolean;
  /** Teaser message text */
  message: string;
  /** Teaser quick action buttons */
  quickActions: TeaserQuickAction[];
}

interface PreviewState {
  /** Current teaser configuration for the preview widget */
  teaserConfig: TeaserConfig;
  /** Bot shown in the preview; `false` until initialized */
  botId: string | false;
  /** Channels of the (live) bot */
  channels: any[];
  /** Index into `channels` of the currently selected channel */
  selectedChannel: number;
  /** channelId of the preview widget channel; `false` until resolved */
  previewChannelId: string | false;
  /** Dock connection info ({ pubsub, topics, config }) for the embedded widget */
  dock: Record<string, any>;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/preview.ts` (namespace `preview`).
 * In-memory only — no `vuexPersist` option (the legacy module was not listed in any `vuexLocal` /
 * `vuexSession` reducer in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - State props `previewChannelId`, `dock`, `channels`, `selectedChannel` each had a legacy getter
 *   of the exact same name (identity passthrough for the first three; `selectedChannel`'s getter
 *   derives a channel object from the index instead of returning the raw index). Pinia merges state
 *   and getters onto ONE store object, so a same-named getter would shadow the state prop — all
 *   four getters are renamed `<name>Getter`: `previewChannelIdGetter`, `dockGetter`,
 *   `channelsGetter`, `selectedChannelGetter`. Consumers that want the raw state value read the
 *   state prop directly (`store.previewChannelId`, `store.dock`, `store.channels`,
 *   `store.selectedChannel`); consumers that want the legacy getter's exact return value use the
 *   `*Getter` name.
 * - Legacy actions `setTeaserMessage`, `addTeaserButton`, `removeTeaserButton`, `setTeaserButtonText`
 *   each had a same-named mutation that the action committed before also dispatching `showTeaser` —
 *   merged into ONE action per name (apply the state change, then call `this.showTeaser()`).
 * - Mutations with no matching action (`setInitialTexts`, `setChannels`, `setSelectedChannel`,
 *   `setPreviewChannel`, `setBotId`, `setTeaser`, `setDock`) become same-named actions (no rename
 *   needed — none of those names collide with a getter or state prop).
 * - Cross-namespace access: legacy `rootGetters['auth/user']` → `useAuthStore().user`; legacy
 *   `dispatch('bots/getBotById', ..., { root: true })` → `useBotsStore().getBotById(...)`. Both
 *   composables are called from inside the action bodies (never at module top level).
 */
export const usePreviewStore = defineStore('preview', {
  state: (): PreviewState => ({
    teaserConfig: {
      // default teaser config
      showTeaser: true,
      showAvatar: true,
      message: '',
      quickActions: [],
    },
    botId: false,
    channels: [],
    selectedChannel: 0,
    previewChannelId: false,
    dock: {},
  }),
  getters: {
    teaser: (state: PreviewState): TeaserConfig => state.teaserConfig,
    teaserButtons: (state: PreviewState): TeaserQuickAction[] => state.teaserConfig.quickActions,
    /**
     * Renamed from legacy getter `previewChannelId` (identity passthrough) — collides with the
     * state prop of the same name.
     */
    previewChannelIdGetter: (state: PreviewState): string | false => state.previewChannelId,
    widgetConfig: (state: PreviewState): any => {
      if (state.channels.length <= state.selectedChannel) {
        return {};
      }

      return state.channels[state.selectedChannel].config;
    },
    /**
     * Renamed from legacy getter `dock` (identity passthrough) — collides with the state prop of
     * the same name.
     */
    dockGetter: (state: PreviewState): Record<string, any> => state.dock,
    getBotId: (state: PreviewState): string | false => state.botId,
    /**
     * Renamed from legacy getter `selectedChannel` — collides with the state prop of the same name
     * (the state prop is the numeric index; this getter derives the channel object at that index).
     *
     * Kept the original condition (`length >= 0` is always true, effectively dead) exactly as in
     * the legacy module.
     */
    selectedChannelGetter: (state: PreviewState): any => {
      if (state.channels.length >= 0) {
        return state.channels[state.selectedChannel];
      }

      return false;
    },
    getWidgetConfig:
      (state: PreviewState) =>
      (channelId: string): any => {
        let channel = state.channels.find((channel) => channel.channelId === channelId);

        if (!channel && state.channels.length > 0) {
          channel = state.channels[0];
        }

        if (channel && channel.channel === 'widget') {
          return channel.config;
        }

        return false;
      },
    /**
     * Renamed from legacy getter `channels` (identity passthrough) — collides with the state prop
     * of the same name.
     */
    channelsGetter: (state: PreviewState): any[] => state.channels,
  },
  actions: {
    /**
     * Initializes the preview widget's bot/channel state for the given bot. If the bot is a
     * staging bot, resolves the live bot it stages for via `bots/getBotById` on the `bots` store.
     *
     * `BotService.getBot` swallows request errors (no access, unknown bot, expired session) and
     * returns `[]`, which has no `channels`. In that case the channel setup is skipped and the page
     * behaves like it does for a logged-out visitor (MOIN-HUB-1X).
     */
    async initPreviewStore(payload: { botId: string; channelId?: string }): Promise<boolean> {
      const authStore = useAuthStore();

      if (authStore.user !== false && authStore.user !== null) {
        let bot: any = await BotService.getBot(payload.botId); // await useBotsStore().getBotById(payload.botId);
        let isLiveBot = false;
        const hasChannels = (b: any) => Array.isArray(b?.channels);
        // if bot is staging bot, fetch the real bot
        if (!hasChannels(bot)) {
          bot = null;
        } else if (bot.stagingBotFor) {
          // get preview widget channelId
          const previewChannel = bot.channels.find((channel: any) => channel.channel === 'widget');
          if (previewChannel) {
            this.setPreviewChannel(previewChannel.channelId);
          }

          bot = await useBotsStore().getBotById(bot.stagingBotFor);
          if (!hasChannels(bot)) {
            bot = null;
          }
        } else {
          isLiveBot = true;
          await useBotsStore().getBotById(payload.botId);
        }

        if (bot) {
          // live channels
          const channelIndex = bot.channels.findIndex(
            (channel: any) => channel.channelId === payload.channelId,
          );

          if (channelIndex > -1) {
            this.setSelectedChannel(channelIndex);
          }

          if (isLiveBot) {
            this.setPreviewChannel(payload.channelId as string);
          }

          if (bot.channels.length > 0) {
            this.setChannels(bot.channels);
          }
        }
      }

      // set bot id
      this.setBotId(payload.botId);
      return true;
    },
    /**
     * Merged from legacy action `setTeaserMessage` (committed the mutation of the same name, then
     * dispatched `showTeaser`) + legacy mutation `setTeaserMessage`.
     */
    setTeaserMessage(message: string) {
      this.teaserConfig.message = message;
      this.showTeaser();
    },
    /**
     * Merged from legacy action `addTeaserButton` + legacy mutation `addTeaserButton`.
     */
    addTeaserButton(payload: TeaserQuickAction) {
      if (!this.teaserConfig.quickActions.find((action) => action.intent === payload.intent)) {
        this.teaserConfig.quickActions.push({ intent: payload.intent, text: payload.text });
      }
      this.showTeaser();
    },
    /**
     * Merged from legacy action `removeTeaserButton` + legacy mutation `removeTeaserButton`.
     */
    removeTeaserButton(intent: string) {
      const idx = this.teaserConfig.quickActions.findIndex((action) => action.intent === intent);

      if (idx >= 0) {
        this.teaserConfig.quickActions.splice(idx, 1);
      }
      this.showTeaser();
    },
    /**
     * Merged from legacy action `setTeaserButtonText` + legacy mutation `setTeaserButtonText`.
     */
    setTeaserButtonText(payload: TeaserQuickAction) {
      const action = this.teaserConfig.quickActions.find((action) => action.intent === payload.intent);

      if (action) {
        action.text = payload.text;
      }
      this.showTeaser();
    },
    connectDock(payload: { pubsub: any; topics: any; config: any; botId: string }) {
      this.setDock(payload);
    },
    async resetWidget(payload: { botId: string; channelId?: string }) {
      // `window.moin` only exists once the widget loader has run; the session cleanup below must
      // still happen when Restart is clicked before that (MOIN-HUB-10).
      await (window as any).moin?.unmount?.();

      let storageKey: string | false = false;
      for (let i = 0; i < localStorage.length && storageKey === false; i++) {
        const key = localStorage.key(i);
        if (key!.startsWith(`knowhere_jwt`) && key!.endsWith(`${payload.botId}`)) {
          storageKey = key!;
        }
      }

      if (storageKey) {
        localStorage.removeItem(storageKey);
      }

      // dispatch('initWidget', { botId, channelId });
      // we do a full reload here, because the widget is not unmounted correctly
    },
    async initWidget(payload: { botId: string; channelId?: string }) {
      // captured so the nested (non-arrow) `function` handlers below — where `this` is rebound by
      // the property setter / event callback invocation — can still reach the store.
      const previewStore = this;
      const { botId } = payload;
      let { channelId } = payload;

      if (this.botId !== botId) {
        this.resetStore();
      }

      if (!channelId) {
        await this.initPreviewStore({ botId, channelId });
        channelId = this.channels[0]?.channelId;
      }

      // Fetch the widget config from the public widget config endpoint (moin-api).
      // This returns the v3 config with language-aware defaults (moreMessages, etc.) already merged.
      // Use the live bot ID since channels belong to the live bot, not the staging bot.
      const liveBotId = useBotsStore().currentBotGetter?.uniqueBotId || botId;
      let widgetApiConfig: any = null;
      if (channelId) {
        try {
          // Time-boxed, so a hanging API falls back instead of delaying the preview widget
          const resp = await fetch(
            `${import.meta.env.VITE_BOTCAST_API}/widget/config/${liveBotId}/${channelId}`,
            { signal: AbortSignal.timeout(5000) },
          );
          if (resp.ok) {
            widgetApiConfig = await resp.json();
          }
        } catch (e) {
          // Fallback to store config if API is unavailable
        }
      }

      // need to proxy the moin object to intercept when addContext is ready
      const proxyWindow: any = {};
      proxyWindow.moin = (window as any).moin;
      Object.defineProperty(window, 'moin', {
        set: function (value: any) {
          if (value === null) return true; // because of unmount

          const moinProxy = new Proxy(value, {
            get: function (target, prop) {
              return target[prop];
            },
            set: function (target, prop, value) {
              target[prop] = value;
              return true;
            },
          });
          proxyWindow.moin = moinProxy;
          if (channelId) {
            const widgetConfig = widgetApiConfig || previewStore.getWidgetConfig(channelId);
            if (widgetConfig) {
              // Hand the widget a plain, deep-cloned snapshot — NOT the live Pinia
              // reactive proxy. Leaking a reactive object into the third-party widget's
              // own processing can set up a reactivity feedback loop that freezes the
              // page. Cloning also avoids mutating store state from this setter.
              let config: any;
              try {
                config = structuredClone(toRaw(widgetConfig));
              } catch {
                config = { ...toRaw(widgetConfig) };
              }
              config.channelId = previewStore.previewChannelIdGetter;
              // Force every preview tab to start closed and ignore the shared
              // localStorage open-state — otherwise opening the widget in one
              // preview tab would auto-open it in every other preview tab.
              config.behaviour = { ...config.behaviour, widgetOpen: 'close' };
              config.useLocalStorage = false;
              proxyWindow.moin.config = config;
            }
          }
          return true;
        },
        get: function () {
          return proxyWindow.moin;
        },
        configurable: true,
      });

      if (channelId) {
        const addContextPayload: Record<string, boolean> = {};
        // add needed context for the selected channel
        addContextPayload['channel_' + channelId] = true;

        // add context when widget is ready
        (window as any).chatWidgetReady = function () {
          (window as any).moin.addContext(addContextPayload);
        };
      }

      const dummyScriptTag = document.createElement('script');
      dummyScriptTag.setAttribute('id', 'moinloader');
      // boolean attribute value kept as in the JS version (coerced to "false" by the DOM)
      dummyScriptTag.setAttribute('async', false as any);
      dummyScriptTag.setAttribute(
        'src',
        `${import.meta.env.VITE_WIDGET_URL}/moin-loader.js?id=` + botId,
      );
      window.document.head.appendChild(dummyScriptTag);
    },
    showTeaser() {
      const moin = (window as any).moin;
      // widget not loaded yet
      if (!moin) return;

      moin.close();
      const qr: TeaserQuickAction[] = [];
      this.teaserConfig.quickActions.forEach((qa: TeaserQuickAction) => {
        qr.push(qa);
      });

      const teaserObj = {
        showTeaser: this.teaserConfig.showTeaser,
        showAvatar: this.teaserConfig.showAvatar,
        message: this.teaserConfig.message,
        quickActions: qr,
        forceShow: true,
      };
      moin.teaser(teaserObj);
    },
    /**
     * Converted from the legacy action `hideTeaser`, which took no context argument and read the
     * module-level `state` const directly instead (a JS-ism that happened to work because Vuex
     * uses that same object as its live module state). Kept the equivalent behavior here — reads
     * the store's current `teaserConfig.message` via `this`.
     */
    hideTeaser() {
      const teaserObj = {
        showTeaser: false,
        showAvatar: false,
        message: this.teaserConfig.message,
        quickActions: [],
        forceShow: true,
      };
      (window as any)?.moin?.teaser(teaserObj);
    },
    sendIntent(payload: { intent: string }) {
      const moin = (window as any).moin;
      // widget not loaded yet
      if (!moin) return;

      moin.open();
      moin.clearQR();
      moin.sendIntent({ intent: payload.intent });
    },
    /**
     * Converted from legacy action `resetStore`, which committed mutations `setTeaser` and
     * `setDock` (both converted to same-named actions below — no naming collisions, so they're
     * called straight through).
     */
    resetStore() {
      // TODO language support
      // de, en

      this.setTeaser({
        // default teaser config
        showTeaser: true,
        showAvatar: true,
        message: 'Wie kann ich Ihnen helfen?',
        quickActions: [
          {
            text: 'Frage stellen',
            intent: 'start',
          },
        ],
      });
      this.setDock({
        pubsub: undefined,
        topics: undefined,
        config: undefined,
        botId: false,
      });
    },
    // Converted from legacy mutations with no matching action (mechanical mutation → action).
    setInitialTexts(payload: { messageText: string; actionText: string }) {
      this.teaserConfig.message = payload.messageText;
      this.teaserConfig.quickActions.push({
        text: payload.actionText,
        intent: 'start',
      });
    },
    setChannels(channels: any[]) {
      this.channels = channels;
    },
    setSelectedChannel(index: number) {
      this.selectedChannel = index;
    },
    setPreviewChannel(channelId: string) {
      this.previewChannelId = channelId;
    },
    setBotId(botId: string) {
      this.botId = botId;
    },
    setTeaser(teaser: TeaserConfig) {
      this.teaserConfig = teaser;
    },
    setDock(payload: { pubsub: any; topics: any; config: any; botId: string | false }) {
      this.dock = {
        config: payload.config,
        pubsub: payload.pubsub,
        topics: payload.topics,
      };

      this.botId = payload.botId;
    },
  },
});
